import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from } from 'rxjs';
import { ESignTemplate } from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { AddEditEsignTemplateModalComponent } from 'src/app/modals/add-edit-esign-template-modal/add-edit-esign-template-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { SignedDocumentService } from 'src/app/services/signed-document.service';
import { ToastService } from 'src/app/services/toast.service';
import { esignTemplatesService } from 'src/app/shared/vive-api/esign-templates.service';
import { EsignTemplatesUpsertRequestDto } from 'src/app/shared/vive-api/vive-api-models';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

@Component({
  selector: 'app-admin-admin-esign-templates',
  templateUrl: './admin-admin-esign-templates.component.html',
  styleUrl: './admin-admin-esign-templates.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminAdminEsignTemplatesComponent {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private ModalService: NgbModal,
    private AuthService: AuthService,
    private SignedDocumentService: SignedDocumentService,
    private ToastService: ToastService
  ) {}

  LoggedInUser = this.AuthService.getUser();

  // Mirrors the roles the v3 upsert and archive endpoints admit. Offering an action
  // to anyone else earns a bare "Forbidden resource" from the API.
  hasValidEditPermission() {
    if (this.LoggedInUser) {
      const { UserAccountRole: { role } = {} } = this.LoggedInUser;

      return (
        role &&
        new Set(['ADMIN_ADMIN', 'ADMIN_PROJECT_MANAGEMENT_TEAM']).has(role)
      );
    }

    return false;
  }

  // TODO load VIVE File.
  // An empty action list drops the column entirely (see BaseGridComponent).
  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: this.hasValidEditPermission() ? ['edit', 'archive'] : []
  };
  TooltipMap: Partial<{ [key in GridActionColumnActions]: string }> = {
    edit: 'Edit template',
    archive: 'Archive template'
  };
  // `attributes` is an ALLOW-LIST on the v2 grid route, not an addition to the
  // default selection — anything absent here simply is not on the row. That is
  // why `mappingFingerprint` has to be named: without it the Field mapping
  // column reads undefined on every row and can only ever render "—".
  SuppliedGridQuery: GenericObject = {
    attributes: [
      'CompanyId',
      'ESignProviderId',
      'templateType',
      'mappingFingerprint'
    ]
  };
  reloadGrid: boolean = true;

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: ESignTemplate;
  }) {
    const { id } = rowData;

    if (!id) {
      this.ToastService.addErrorToast(
        'Error',
        'ESign Template does not have an id.',
        { returnError: true }
      );
      return;
    }

    if (action === 'edit') {
      this.addEditTemplate(rowData);
    }

    if (action === 'archive') {
      this.archiveTemplate(id);
    }
  }

  // Archiving a template cascades to the signed documents that reference it, so tell
  // the admin how many records they are about to take with them before they confirm.
  // There is no v3 count for this, so we use the same v2 signed-document count the
  // signed-documents grid uses. The API excludes already-archived records, making the
  // number returned exactly the set that will be archived.
  private archiveTemplate(esignTemplateId: number): void {
    this.SignedDocumentService.getSignedDocumentCount({
      ESignTemplateId: esignTemplateId
    }).subscribe({
      next: (signedDocumentCount: number) =>
        this.confirmArchiveTemplate(esignTemplateId, signedDocumentCount),
      // The count is descriptive only — a failed lookup must not block archiving.
      // Fall back to copy without a number rather than showing one we can't stand behind.
      error: () => this.confirmArchiveTemplate(esignTemplateId, null)
    });
  }

  private confirmArchiveTemplate(
    esignTemplateId: number,
    signedDocumentCount: number | null
  ): void {
    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { backdrop: 'static', centered: true, size: 'md' }
    );

    Object.assign(componentInstance, {
      title: 'Archive template',
      displayText: this.buildArchiveConfirmText(signedDocumentCount),
      continueButtonText: 'Archive template',
      confirmTone: 'danger'
    });

    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          from(
            esignTemplatesService.archiveESignTemplate({
              pathParams: { esignTemplateId },
              options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
            })
          ).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully archived the template.'
              );
              this.reloadGrid = true;
              this._cdr.markForCheck();
            }
          });
        }
      }
    });
  }

  private buildArchiveConfirmText(signedDocumentCount: number | null): string {
    if (signedDocumentCount === null) {
      return (
        'Archiving this template will also archive any signed document records that ' +
        'use it. Vendors who signed it will need to sign the new template. Continue?'
      );
    }

    if (signedDocumentCount === 0) {
      return (
        'No signed document records currently use this template, so none will be ' +
        'archived with it. Continue?'
      );
    }

    const records =
      signedDocumentCount === 1
        ? '1 signed document record'
        : `${signedDocumentCount} signed document records`;

    return (
      `Archiving this template will also archive ${records}. Vendors who signed it ` +
      'will need to sign the new template. Continue?'
    );
  }

  addEditTemplate(template?: ESignTemplate) {
    const { componentInstance, result } = this.ModalService.open(
      AddEditEsignTemplateModalComponent,
      { backdrop: 'static', size: 'lg' }
    );

    if (template) {
      Object.assign(componentInstance, { ESignTemplate: template });
    }

    from(result).subscribe({
      next: (ESignTemplate: EsignTemplatesUpsertRequestDto) => {
        // Publishing a file is the one action here with a population-wide
        // consequence: it changes the template checksum, which puts every vendor
        // of that client on the re-sign list at their next renewal. Archiving has
        // always had a confirmation for the same reason; replacing needs one too.
        // A MAPPING change is population-wide too, even with no new file: it
        // moves the effective template checksum on its own, so it earns the same
        // confirmation a file replacement does.
        if (ESignTemplate.TemplateFile || ESignTemplate.fields?.length) {
          this.confirmPublishTemplateFile(ESignTemplate, template);
          return;
        }

        this.saveTemplate(ESignTemplate, template);
      }
    });
  }

  private confirmPublishTemplateFile(
    ESignTemplate: EsignTemplatesUpsertRequestDto,
    template?: ESignTemplate
  ): void {
    const mappingFieldCount = ESignTemplate.fields?.length ?? 0;

    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { backdrop: 'static', centered: true, size: 'md' }
    );

    Object.assign(componentInstance, {
      title: template ? 'Publish new version' : 'Publish template',
      displayText: template
        ? 'This file becomes the active version of the template. Vendors who ' +
          'signed the current version keep their signed agreement and stay ' +
          'compliant, but each will be asked to sign this version at their next ' +
          'renewal. Continue?'
        : 'This file becomes the active version for the selected client and ' +
          'template type. If that client already has an active template of this ' +
          'type, this replaces it and its vendors will be asked to sign the new ' +
          'version at their next renewal. Continue?',
      continueButtonText: 'Publish',
      // The count belongs HERE, at the irreversible step, rather than beside a
      // Save button the admin has already left behind. Asked for the template
      // being edited; a brand-new one has no signatures yet.
      ...(mappingFieldCount > 0 &&
        template && {
          consequenceText:
            `You also set ${mappingFieldCount} field ` +
            `${mappingFieldCount === 1 ? 'mapping' : 'mappings'}. That changes ` +
            'what the signed document says, so every vendor of this client will ' +
            'be asked to re-sign at their next renewal.',
          // VPDW-4380 open question 5: whether a mapping change needs client
          // sign-off is undecided, so the checkbox records that the admin says
          // it happened rather than asserting VIVE verified it.
          acknowledgementText: "I've confirmed this change with the client"
        })
    });

    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          this.saveTemplate(ESignTemplate, template);
        }
      }
    });
  }

  private saveTemplate(
    ESignTemplate: EsignTemplatesUpsertRequestDto,
    template?: ESignTemplate
  ): void {
    from(
      esignTemplatesService.upsertESignTemplate({
        data: ESignTemplate,
        options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
      })
    ).subscribe({
      next: () => {
        this.ToastService.addSuccessToast(
          'Success',
          `Successfully ${template ? 'edited' : 'added'} ESign Template.`
        );
        this.reloadGrid = true;
        this._cdr.markForCheck();
      }
    });
  }
}
